import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Textarea,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { createWorkspace, updateWorkspace } from "api";
import { Constants, ConstantValues } from "common/constants";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { IAzureUser } from "models/IAzureUser";
import { IUser } from "models/IUser";
import { IUserDetails } from "models/IUserDetails";
import { ICreateOrUpdateWorkspace, IWorkspace } from "models/IWorkspace";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { getFieldValidation } from "utils/dialogUtils";
import {
  isNullUndefinedOrEmpty,
  mapAzureUsersToModel,
} from "utils/generalUtils";
import {
  addWorkspace,
  updateWorkspaceInSlice,
} from "../../../redux/workspacesSlice";
import {
  DIALOG_LABELS,
  ERROR_MESSAGES,
  FIELD_LIMITS,
  IEditWorkspaceDialogProps,
  PICKER_IDS,
} from "./EditWorkspaceDialog.types";

import {
  deduplicateOptions,
  optionToUserDetails,
  userDetailsToOption,
  validateDuplicateName,
  validateOwners,
  validateRequiredFields,
  validateSelfRemoval,
} from "utils/workspaceFormValidation";
export {
  deduplicateOptions,
  optionToUserDetails,
  userDetailsToOption,
  validateDuplicateName,
  validateOwners,
  validateRequiredFields,
  validateSelfRemoval,
} from "utils/workspaceFormValidation";

export const EditWorkspaceDialog = (
  props: IEditWorkspaceDialogProps,
): JSX.Element => {
  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );
  const allWorkspaces = useSelector(
    (state: RootState) => state.workspacesSlice.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );

  const dispatch = useDispatch();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();

  const isEdit = !isNullUndefinedOrEmpty(props.workspace);
  const isPlatformAdmin = currentUser.roles.includes(
    Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
  );
  const isOwner = props.workspace?.owners?.some(
    (owner) => owner.userId === currentUser.id,
  );
  const canEditOwners = isPlatformAdmin || (props.allowOwnerEdit && isOwner);

  const [name, setName] = useState<string>(ConstantValues.EMPTY_STRING);
  const [clientName, setClientName] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [teamMembers, setTeamMembers] = useState<CustomOption[]>([]);
  const [owners, setOwners] = useState<CustomOption[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect((): void => {
    if (props.workspace) {
      setName(props.workspace.name);
      setClientName(props.workspace.clientName);
      setTeamMembers(
        (props.workspace.teamMembers || []).map(userDetailsToOption),
      );
      setOwners((props.workspace.owners || []).map(userDetailsToOption));
      return;
    }

    setOwners([
      {
        id: currentUser.id,
        key: currentUser.id,
        text: currentUser.displayName,
      },
    ]);
  }, [props.workspace, currentUser]);

  const handleSelectedTeamMembers = (selected: CustomOption[]): void => {
    setTeamMembers(deduplicateOptions(selected));
    if (fieldErrors.teamMembers) {
      setFieldErrors((prev) => ({
        ...prev,
        teamMembers: ConstantValues.EMPTY_STRING,
      }));
    }
  };

  const handleOwnersChange = (selected: CustomOption[]): void => {
    setOwners(deduplicateOptions(selected));
    if (fieldErrors.owners) {
      setFieldErrors((prev) => ({
        ...prev,
        owners: ConstantValues.EMPTY_STRING,
      }));
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setName(e.target.value);
    if (fieldErrors.name) {
      setFieldErrors((prev) => ({
        ...prev,
        name: ConstantValues.EMPTY_STRING,
      }));
    }
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    setClientName(e.target.value);
    if (fieldErrors.clientName) {
      setFieldErrors((prev) => ({
        ...prev,
        clientName: ConstantValues.EMPTY_STRING,
      }));
    }
  };

  const readResourcesForPickerFieldFiltered = useCallback(
    async (searchedValue: string): Promise<IAzureUser[]> => {
      const mappedUsers = mapAzureUsersToModel(users);
      return mappedUsers.filter((resource: IAzureUser) =>
        resource.Name?.toLowerCase().includes(searchedValue.toLowerCase()),
      );
    },
    [users],
  );

  const handleSubmit = async (): Promise<void> => {
    const errors: Record<string, string> = {};

    if (name.length > FIELD_LIMITS.NAME_MAX_LENGTH) {
      errors.name = `Workspace Name must be ${FIELD_LIMITS.NAME_MAX_LENGTH} characters or fewer.`;
    }
    if (clientName.length > FIELD_LIMITS.DESCRIPTION_MAX_LENGTH) {
      errors.clientName = `Description must be ${FIELD_LIMITS.DESCRIPTION_MAX_LENGTH} characters or fewer.`;
    }

    Object.assign(errors, validateRequiredFields(name, clientName));

    const nameChanged =
      !props.workspace ||
      name.trim().toLowerCase() !== props.workspace.name.trim().toLowerCase();

    if (nameChanged) {
      Object.assign(
        errors,
        validateDuplicateName(name, allWorkspaces, props.workspace?.id),
      );
    }

    Object.assign(errors, validateOwners(owners));
    Object.assign(
      errors,
      validateSelfRemoval(
        currentUser.id,
        props.workspace?.teamMembers?.map((m) => m.userId) || [],
        teamMembers
          .map((m) => m.id)
          .filter((id): id is string => id !== undefined),
      ),
    );

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSaving(true);
    try {
      const ownerIds = new Set(owners.map((o) => o.id));
      const members: IUserDetails[] = teamMembers
        .filter((member) => member.id && !ownerIds.has(member.id))
        .map(optionToUserDetails);

      const workspacePayload: ICreateOrUpdateWorkspace = {
        id: props.workspace?.id,
        name,
        clientName,
        teamMembers: members,
        owners: owners.map(optionToUserDetails),
      };

      let resultWorkspace: IWorkspace;

      if (props.workspace) {
        await updateWorkspace({
          id: props.workspace.id,
          workspace: workspacePayload,
        });
        resultWorkspace = {
          ...props.workspace,
          ...workspacePayload,
          updatedAt: String(Math.floor(Date.now() / 1000)),
        };
        dispatch(updateWorkspaceInSlice(resultWorkspace));
      } else {
        resultWorkspace = await createWorkspace({
          workspace: workspacePayload,
        });
        dispatch(addWorkspace(resultWorkspace));
      }

      props.onDismiss(true, resultWorkspace);
    } catch (e) {
      console.error("Error saving workspace:", e);
      setFieldErrors({
        general: ERROR_MESSAGES.SAVE_ERROR,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const nameValidation = getFieldValidation(
    name.length,
    FIELD_LIMITS.NAME_MAX_LENGTH,
    FIELD_LIMITS.NAME_WARNING_THRESHOLD,
  );
  const descValidation = getFieldValidation(
    clientName.length,
    FIELD_LIMITS.DESCRIPTION_MAX_LENGTH,
    FIELD_LIMITS.DESCRIPTION_WARNING_THRESHOLD,
  );

  const hasIncompleteDetails =
    !name.trim() ||
    !clientName.trim() ||
    nameValidation.state === "error" ||
    descValidation.state === "error";

  return (
    <Dialog
      open={props.isOpen}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open) props.onDismiss(false);
      }}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={() => props.onDismiss(false)}
              />
            }
          >
            {isEdit ? DIALOG_LABELS.EDIT_TITLE : DIALOG_LABELS.CREATE_TITLE}
          </DialogTitle>
          <DialogContent className={contentStyles.formContainer}>
            {isSaving && (
              <div className={contentStyles.formLoadingOverlay}>
                <Spinner
                  label={`${isEdit ? DIALOG_LABELS.SAVING : DIALOG_LABELS.CREATING} workspace...`}
                />
              </div>
            )}
            {fieldErrors.general && (
              <div className={contentStyles.feedbackErrorBox} role="alert">
                {fieldErrors.general}
              </div>
            )}

            <Field
              label={DIALOG_LABELS.WORKSPACE_NAME}
              required
              validationState={
                fieldErrors.name ? "error" : nameValidation.state
              }
              validationMessage={fieldErrors.name || nameValidation.message}
            >
              <Input
                value={name}
                onChange={handleNameChange}
                placeholder="Enter workspace name..."
                disabled={isSaving}
              />
            </Field>

            <Field
              label={DIALOG_LABELS.DESCRIPTION}
              required
              validationState={
                fieldErrors.clientName ? "error" : descValidation.state
              }
              validationMessage={
                fieldErrors.clientName || descValidation.message
              }
            >
              <Textarea
                value={clientName}
                onChange={handleDescriptionChange}
                resize="vertical"
                rows={6}
                placeholder="Enter workspace description..."
                disabled={isSaving}
              />
            </Field>

            {canEditOwners && (
              <Field
                label={DIALOG_LABELS.OWNERS}
                required
                validationState={fieldErrors.owners ? "error" : "none"}
                validationMessage={fieldErrors.owners}
              >
                <PeoplePicker
                  id={PICKER_IDS.OWNERS}
                  handleSelectedItems={handleOwnersChange}
                  readResourcesForPickerFieldFiltered={
                    readResourcesForPickerFieldFiltered
                  }
                  selectedOptions={owners}
                  disabled={isSaving}
                  required={true}
                  hasOnlyOurUsers={true}
                />
              </Field>
            )}

            <Field
              label={DIALOG_LABELS.TEAM_MEMBERS}
              validationState={fieldErrors.teamMembers ? "error" : "none"}
              validationMessage={fieldErrors.teamMembers}
            >
              <PeoplePicker
                id={PICKER_IDS.TEAM_MEMBERS}
                handleSelectedItems={handleSelectedTeamMembers}
                disabled={isSaving}
                hasOnlyOurUsers={true}
                readResourcesForPickerFieldFiltered={
                  readResourcesForPickerFieldFiltered
                }
                selectedOptions={teamMembers}
              />
            </Field>
          </DialogContent>
        </DialogBody>
        {!isSaving && (
          <div className={footerStyles.dialogFooter}>
            <DialogFooterAction
              icon={<CheckmarkRegular />}
              onClick={handleSubmit}
              dimmed={hasIncompleteDetails}
              tooltip="Save"
            />
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
